import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, concatMap, map } from 'rxjs/operators';

import { AppConfig, CMPS_APP_CONFIG } from '../../../app.config';
import {
    ColumnConfigItem,
    mapper,
} from '../../../coaching-insight/oa/models/coaching.global.model';
import { sortByFLMi2 } from '../function/sort-by-fl-mi2.function';
import { sortByFLTotalDirectReport } from '../function/sort-by-fl-total-direct-report.function';
import { getFrontlineColumnConfigItemsConfig } from '../mi2/frontline-mi2-column-config';
import {
    FLMI2Person,
    FLMi2TeamTransformer,
    FLMi2Transformer,
} from '../model/flmi2.model';
import { SortDirection } from '../model/sort-direction.model';
// import { Mi2GridSort, SortDirection } from '../model/sorting.model';

@Injectable()
export class FrontlineMI2Service {
    _flMI2: BehaviorSubject<FLMI2Person> = new BehaviorSubject<FLMI2Person>(
        null,
    );

    public _activeMember: BehaviorSubject<FLMI2Person> =
        new BehaviorSubject<FLMI2Person>(null);

    public _forceOpenTeamMI2: BehaviorSubject<boolean> =
        new BehaviorSubject<boolean>(null);

    public _rowExpanded: BehaviorSubject<boolean> =
        new BehaviorSubject<boolean>(null);

    // _oaSortDirection: BehaviorSubject<SortDirection> = new BehaviorSubject<SortDirection>(null);
    // _expectedSortDirection: BehaviorSubject<SortDirection> = new BehaviorSubject<SortDirection>(SortDirection.DESC);

    _mi2SortDirection: SortDirection;
    _expectedSortDirection: SortDirection;

    // key-value for an observable and primary key of metrics
    // metric icon state observables
    private _flMI2ColumnConfig: BehaviorSubject<ColumnConfigItem[]>;
    private readonly _metricColumnConfig: BehaviorSubject<ColumnConfigItem[]>;

    // current state of the Frontline OA component section
    private _cachedFlMI2: any;

    constructor(
        private readonly _http: HttpClient,
        @Inject(CMPS_APP_CONFIG) private readonly _appConfig: AppConfig,
        private readonly _translateService: TranslateService,
    ) {}

    public hasPendingRefresh: BehaviorSubject<boolean> =
        new BehaviorSubject<boolean>(false);

    resetWidget(): void {
        this._flMI2.next(null);
        this._activeMember.next(null);
        if (this._flMI2ColumnConfig) {
            this._flMI2ColumnConfig.next(null);
        }
    }

    get flmi2(): Observable<FLMI2Person> {
        return this._flMI2.asObservable();
    }

    get mi2columns(): Observable<ColumnConfigItem[]> {
        return this._flMI2ColumnConfig.asObservable();
    }

    get expected(): number {
        return 10;
    }

    get rowExpanded(): Observable<boolean> {
        return this._rowExpanded.asObservable();
    }

    get activeMember(): Observable<FLMI2Person> {
        return this._activeMember.asObservable();
    }

    get forceOpenTeamMi2(): Observable<boolean> {
        return this._forceOpenTeamMI2.asObservable();
    }

    get isTeamMi2Open(): boolean {
        return this._forceOpenTeamMI2.getValue();
    }

    get isTeamMi2RowExpanded(): boolean {
        return this._rowExpanded.getValue();
    }

    get cachedFlmi2(): string {
        return this._cachedFlMI2;
    }

    getDirection(type: string): SortDirection {
        return type === 'oaSort'
            ? this._mi2SortDirection
            : this._expectedSortDirection;
    }

    setRowExpanded(val: boolean): void {
        this._rowExpanded.next(val);
    }

    getColumns(): ColumnConfigItem[] {
        return this._flMI2ColumnConfig.getValue();
    }

    retrieveFLMi2(
        userId: any,
        mapToGlobalServiceData = true,
    ): Observable<FLMI2Person> {
        return this._http
            .get(`${this._appConfig.API_ENDPOINTS.SCORECARD}/mi2/flmi2`, {
                params: new HttpParams().set('userId', userId.toString()),
            })
            .pipe(
                map((data) => mapper(data)),
                concatMap((transformedData) =>
                    this._mapResponse(transformedData, mapToGlobalServiceData),
                ),
                catchError((error) => {
                    console.error(
                        'Error fetching and transforming FLMI2 data',
                        error,
                    );
                    this._mapResponse(null, mapToGlobalServiceData); // Handle error scenario
                    return throwError(
                        () => new Error('Error processing FLMI2 data'),
                    );
                }),
            );
    }

    retrieveFLMi2PeriodDates(userId: number): Observable<any> {
        const req1 = this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.SCORECARD}/mi2/flmi2-period-dates`,
                { params: this.generateUserIdParams(userId) },
            )
            .pipe(map(mapper));
        return req1;
    }

    retrieveTeamFLMi2(userId: any): Observable<any> {
        return this._http
            .get(`${this._appConfig.API_ENDPOINTS.SCORECARD}/mi2/subflmi2`, {
                params: new HttpParams().set('managerId', userId.toString()),
            })
            .pipe(
                map(mapper),
                map((data) => this._mapTeamResponse(data)),
            );
    }

    setForceOpenTeam(state: boolean): void {
        this._forceOpenTeamMI2.next(state);
    }

    setCurrentFlmi2State(f: FLMI2Person): void {
        if (f) this._flMI2.next(f);
    }

    private _mapResponse(
        data: any,
        mapToGlobalServiceData = true,
    ): Observable<FLMI2Person | null> {
        return new Observable<FLMI2Person | null>((sub) => {
            const flmi2 = new FLMi2Transformer().transform(data);

            if (!flmi2 || flmi2.userId == null) {
                this._flMI2.next(null);
                sub.next(null);
                sub.complete();
            } else {
                if (mapToGlobalServiceData) {
                    this._flMI2.next(flmi2);
                }
                sub.next(flmi2);
                sub.complete();
            }
        });
    }

    private _mapTeamResponse(data: any): FLMI2Person[] {
        const leader: FLMI2Person = this._flMI2.getValue(); // there is only 1 leader per FLOA view
        const flmi2 = new FLMi2TeamTransformer(leader).transform(data);

        // use the current sort, but don't change direction
        if (
            (this._mi2SortDirection === SortDirection.DEFAULT &&
                this._expectedSortDirection === SortDirection.DEFAULT) ||
            (this._expectedSortDirection !== SortDirection.DEFAULT &&
                this._mi2SortDirection === SortDirection.DEFAULT)
        ) {
            flmi2.sort(sortByFLTotalDirectReport(this._expectedSortDirection));
        } else if (
            this._mi2SortDirection !== SortDirection.DEFAULT &&
            this._expectedSortDirection === SortDirection.DEFAULT
        ) {
            flmi2.sort(sortByFLMi2(this._mi2SortDirection));
        }

        if (!flmi2) return null;

        return flmi2;
    }

    initializeColumnConfig(): void {
        this._flMI2ColumnConfig = new BehaviorSubject<ColumnConfigItem[]>(
            getFrontlineColumnConfigItemsConfig(this._translateService),
        );
        this._mi2SortDirection = SortDirection.DESC;
        this._expectedSortDirection = SortDirection.DEFAULT;
    }

    traverse(
        type: string,
        dir: SortDirection,
        subordinates: FLMI2Person[],
    ): void {
        if (!subordinates || !subordinates.length) return;

        const expandedsubs = subordinates.filter(
            (f) => f.isExpanded && f.subOrdinates && f.subOrdinates.length,
        );
        if (expandedsubs.length) {
            expandedsubs.forEach((q) => {
                this.traverse(type, dir, q.subOrdinates);
            });
        } else {
            if (type === 'oascore') {
                subordinates.sort(sortByFLMi2(dir)); // default Sort for all
            } else if (type === 'expected') {
                if (dir === SortDirection.DEFAULT) {
                    subordinates.sort(sortByFLTotalDirectReport(dir)); // default Sort for all
                } else {
                    subordinates.sort(sortByFLTotalDirectReport(dir));
                }
            }
        }
    }

    sortExpected(forceDirection?: SortDirection): SortDirection {
        if (this._expectedSortDirection === SortDirection.DESC) {
            this._expectedSortDirection = SortDirection.ASC;
            this._mi2SortDirection = SortDirection.DEFAULT;
        } else if (this._expectedSortDirection === SortDirection.ASC) {
            this._expectedSortDirection = SortDirection.DEFAULT;
            this._mi2SortDirection = SortDirection.DESC;
        } else if (this._expectedSortDirection === SortDirection.DEFAULT) {
            this._expectedSortDirection = SortDirection.DESC;
            this._mi2SortDirection = SortDirection.DEFAULT;
        }

        if (forceDirection != null) {
            this._expectedSortDirection = forceDirection;
        }

        return this._expectedSortDirection;
    }

    sortFLMi2(forceDirection?: SortDirection): SortDirection {
        this._mi2SortDirection =
            this._mi2SortDirection === SortDirection.DESC
                ? SortDirection.ASC
                : SortDirection.DESC;

        this._expectedSortDirection = SortDirection.DEFAULT;

        if (forceDirection != null) {
            this._mi2SortDirection = forceDirection;
        }

        return this._mi2SortDirection;
    }

    sortTeamBy(
        type: string,
        force?: SortDirection,
        flmi2?: FLMI2Person,
    ): SortDirection {
        if (flmi2 == null) {
            flmi2 = this._flMI2.getValue();
        }

        const dir =
            type === 'oascore'
                ? this.sortFLMi2(force)
                : this.sortExpected(force);

        this.traverse(type, dir, flmi2.subOrdinates);

        return dir;
    }

    saveCurrentViewState(): void {
        const floa = this._flMI2.getValue();
        this._cachedFlMI2 = JSON.stringify(floa);
    }

    restoreViewState(): void {
        if (typeof this._cachedFlMI2 === 'string') {
            const x: FLMI2Person = JSON.parse(this._cachedFlMI2);
            this._flMI2.next(x);
            this._cachedFlMI2 = null;
        }
    }

    resetCachedFLMi2(): void {
        this._cachedFlMI2 = null;
    }

    generateParams(
        userId: any,
        periodTypeId: any,
        currentDate: Date,
    ): HttpParams {
        const params = new HttpParams().set('userId', userId.toString());
        // .set('periodTypeId', periodTypeId.toString());
        // if (currentDate) {
        //     params.set('currentDate', typeof currentDate === 'string' ? currentDate : (currentDate).toJSON());
        // }
        return params;
    }

    generateUserIdParams(userId: any): HttpParams {
        const params = new HttpParams().set('userId', userId.toString());
        return params;
    }
}
